import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  CheckCircle, XCircle, Clock, User, Calendar, AlertCircle,
  ChevronDown, ChevronUp, Pencil, Trash2, Briefcase, History, MessageSquare,
  Users, X, Undo2, RefreshCw, Sparkles,
} from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { toast } from 'sonner';
import { getApiError } from '../lib/utils';

const fmtHHMM = (decimalHours) => {
  const h = Math.floor(decimalHours);
  const m = Math.round((decimalHours - h) * 60);
  return `${h}:${String(m).padStart(2, '0')}`;
};

const groupTimesheets = (list) => {
  const groups = {};
  for (const ts of list) {
    const key = `${ts.user_id}|${ts.week_start}`;
    if (!groups[key]) {
      groups[key] = {
        id: key,
        key,
        user_id: ts.user_id,
        week_start: ts.week_start,
        week_end: ts.week_end,
        status: ts.status,
        total_hours: 0,
        billable_hours: 0,
        rejection_reason: null,
        recall_reason: null,
        submitted_at: ts.submitted_at,
        approved_at: ts.approved_at,
        approved_by: ts.approved_by,
        timesheets: [],
      };
    }
    const g = groups[key];
    g.total_hours = Math.round((g.total_hours + (ts.total_hours || 0)) * 100) / 100;
    g.billable_hours = Math.round((g.billable_hours + (ts.billable_hours || 0)) * 100) / 100;
    g.timesheets.push(ts);
    if (ts.rejection_reason) g.rejection_reason = ts.rejection_reason;
    if (ts.recall_reason) g.recall_reason = ts.recall_reason;
  }
  return Object.values(groups);
};

const ApprovalsPage = () => {
  const { api, canApprove, user } = useAuth();
  const location = useLocation();
  const [timesheets, setTimesheets] = useState([]);
  const [users, setUsers] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedTimesheet, setSelectedTimesheet] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [activeTab, setActiveTab] = useState('pending');
  const [selectedUserId, setSelectedUserId] = useState('all');

  // For detail view
  const [expandedId, setExpandedId] = useState(null);
  const [detailData, setDetailData] = useState({}); // { [timesheet_id]: { entries, projectBreakdown } }
  const [detailLoading, setDetailLoading] = useState({});

  // For edit entry
  const [editEntry, setEditEntry] = useState(null);
  const [editForm, setEditForm] = useState({ hours: '', description: '', is_billable: true, blockers: '' });
  const [editSaving, setEditSaving] = useState(false);

  // For delete entry
  const [deleteEntry, setDeleteEntry] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // For approve/reject loading
  const [approvingId, setApprovingId] = useState(null);
  const [rejectLoading, setRejectLoading] = useState(false);
  const [pendingFlaggedEntries, setPendingFlaggedEntries] = useState([]);

  // For workflow history
  const [workflowTimesheet, setWorkflowTimesheet] = useState(null);


  useEffect(() => {
    fetchData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  const fetchData = async () => {
    setLoading(true);
    setDetailData({});
    try {
      const [timesheetsRes, usersRes, selfRes] = await Promise.all([
        api.get('/timesheets'),
        api.get('/users'),
        api.get(`/timesheets?user_id=${user?.id}`),
      ]);
      // Merge team timesheets + own timesheets, deduplicate by id
      const seen = new Set();
      const all = [];
      for (const ts of [...timesheetsRes.data, ...selfRes.data]) {
        if (!seen.has(ts.id)) { seen.add(ts.id); all.push(ts); }
      }
      all.sort((a, b) => b.week_start.localeCompare(a.week_start));
      setTimesheets(all);
      const usersMap = {};
      usersRes.data.forEach(u => { usersMap[u.id] = u; });
      setUsers(usersMap);
    } catch (err) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const fetchGroupDetail = async (groupKey, timesheetIds) => {
    if (detailData[groupKey]) return;
    setDetailLoading(prev => ({ ...prev, [groupKey]: true }));
    try {
      const results = await Promise.all(timesheetIds.map(id => api.get(`/timesheets/${id}`)));
      const rawEntries = results.flatMap(r => r.data.entries || []);

      // Merge entries with same project+date across all timesheets in the group
      const entryMap = {};
      for (const e of rawEntries) {
        const key = `${e.project_id}|${e.date}`;
        if (!entryMap[key]) {
          entryMap[key] = { ...e };
        } else {
          entryMap[key].hours = Math.round((entryMap[key].hours + e.hours) * 100) / 100;
          if (!entryMap[key].description && e.description) entryMap[key].description = e.description;
          if (!entryMap[key].blockers && e.blockers) entryMap[key].blockers = e.blockers;
        }
      }
      const entries = Object.values(entryMap).sort((a, b) =>
        a.date.localeCompare(b.date) || a.project_name?.localeCompare(b.project_name)
      );

      const breakdown = {};
      for (const e of entries) {
        const pid = e.project_id;
        if (!breakdown[pid]) {
          breakdown[pid] = { project_id: pid, project_name: e.project_name || 'Unknown Project', total_hours: 0, billable_hours: 0 };
        }
        breakdown[pid].total_hours += e.hours;
        if (e.is_billable !== false) breakdown[pid].billable_hours += e.hours;
      }

      setDetailData(prev => ({
        ...prev,
        [groupKey]: { entries, projectBreakdown: Object.values(breakdown) },
      }));
    } catch (err) {
      toast.error('Failed to load timesheet details');
    } finally {
      setDetailLoading(prev => ({ ...prev, [groupKey]: false }));
    }
  };

  const toggleExpand = (groupKey, timesheetIds) => {
    if (expandedId === groupKey) {
      setExpandedId(null);
    } else {
      setExpandedId(groupKey);
      fetchGroupDetail(groupKey, timesheetIds);
    }
  };

  const handleApprove = async (groupKey, timesheetIds) => {
    setApprovingId(groupKey);
    try {
      await Promise.all(timesheetIds.map(id =>
        api.post(`/timesheets/${id}/approve`, { timesheet_id: id, status: 'approved' })
      ));
      setTimesheets(prev => prev.map(t =>
        timesheetIds.includes(t.id) ? { ...t, status: 'approved', approved_at: new Date().toISOString() } : t
      ));
      toast.success('Timesheet approved');
    } catch (err) {
      toast.error('Failed to approve timesheet');
    } finally {
      setApprovingId(null);
    }
  };

  const handleReject = async () => {
    if (!selectedTimesheet || !rejectReason.trim()) {
      toast.error('Please provide a rejection reason');
      return;
    }
    setRejectLoading(true);
    try {
      const ids = selectedTimesheet._ids;
      const reason = rejectReason;
      await Promise.all(ids.map(id =>
        api.post(`/timesheets/${id}/approve`, {
          timesheet_id: id,
          status: 'rejected',
          comments: reason,
          flagged_entries: pendingFlaggedEntries,
        })
      ));
      setTimesheets(prev => prev.map(t =>
        ids.includes(t.id) ? { ...t, status: 'rejected', rejection_reason: reason } : t
      ));
      toast.success('Timesheet rejected');
      setShowRejectDialog(false);
      setSelectedTimesheet(null);
      setRejectReason('');
    } catch (err) {
      toast.error('Failed to reject timesheet');
    } finally {
      setRejectLoading(false);
    }
  };

  const openRejectDialog = (group, prefillReason = '', flaggedEntries = []) => {
    setSelectedTimesheet({ _ids: group.timesheets.map(t => t.id) });
    setRejectReason(prefillReason);
    setPendingFlaggedEntries(flaggedEntries);
    setShowRejectDialog(true);
  };

  const handleDeleteEntry = (entry) => {
    setDeleteEntry(entry);
  };

  const confirmDeleteEntry = async () => {
    if (!deleteEntry) return;
    setDeleteLoading(true);
    try {
      await api.delete(`/time-entries/${deleteEntry.id}`);
      toast.success('Entry deleted');
      const timesheetId = deleteEntry.timesheet_id;
      const deletedTs = timesheets.find(t => t.id === timesheetId);
      const groupKey = deletedTs ? `${deletedTs.user_id}|${deletedTs.week_start}` : timesheetId;
      const groupIds = deletedTs
        ? timesheets.filter(t => t.user_id === deletedTs.user_id && t.week_start === deletedTs.week_start).map(t => t.id)
        : [timesheetId];
      setDetailData(prev => {
        const next = { ...prev };
        delete next[groupKey];
        return next;
      });
      fetchGroupDetail(groupKey, groupIds);
    } catch (err) {
      toast.error('Failed to delete entry');
    } finally {
      setDeleteLoading(false);
      setDeleteEntry(null);
    }
  };

  const openEditEntry = (entry) => {
    setEditEntry(entry);
    setEditForm({
      hours: entry.hours,
      description: entry.description || '',
      is_billable: entry.is_billable !== false,
      blockers: entry.blockers || '',
    });
  };

  const saveEditEntry = async () => {
    if (!editEntry) return;
    const hours = parseFloat(editForm.hours);
    if (isNaN(hours) || hours <= 0) {
      toast.error('Enter valid hours');
      return;
    }
    setEditSaving(true);
    try {
      await api.put(`/time-entries/${editEntry.id}`, {
        hours,
        description: editForm.description || null,
        is_billable: editForm.is_billable,
        blockers: editForm.blockers || null,
      });
      toast.success('Entry updated');
      // Refresh detail and timesheet list
      const timesheetId = editEntry.timesheet_id;
      const editedTs = timesheets.find(t => t.id === timesheetId);
      const groupKey = editedTs ? `${editedTs.user_id}|${editedTs.week_start}` : timesheetId;
      const groupIds = editedTs
        ? timesheets.filter(t => t.user_id === editedTs.user_id && t.week_start === editedTs.week_start).map(t => t.id)
        : [timesheetId];
      setDetailData(prev => {
        const next = { ...prev };
        delete next[groupKey];
        return next;
      });
      await fetchGroupDetail(groupKey, groupIds);
      // Update totals in timesheets list
      const tsRes = await api.get(`/timesheets/${timesheetId}`);
      setTimesheets(prev => prev.map(t => t.id === timesheetId
        ? { ...t, total_hours: tsRes.data.total_hours, billable_hours: tsRes.data.billable_hours }
        : t
      ));
      setEditEntry(null);
    } catch (err) {
      toast.error(getApiError(err, 'Failed to update entry'));
    } finally {
      setEditSaving(false);
    }
  };

  const filterTimesheets = (status) => {
    let list = timesheets;
    if (status === 'pending') list = list.filter(t => t.status === 'submitted');
    else if (status === 'approved') list = list.filter(t => t.status === 'approved');
    else if (status === 'rejected') list = list.filter(t => t.status === 'rejected');
    if (selectedUserId && selectedUserId !== 'all') list = list.filter(t => t.user_id === selectedUserId);
    return list;
  };

  const uniqueUserIds = [...new Set(timesheets.map(t => t.user_id))];
  const userOptions = uniqueUserIds
    .map(id => users[id])
    .filter(Boolean)
    .sort((a, b) => `${a.first_name} ${a.last_name}`.localeCompare(`${b.first_name} ${b.last_name}`));

  const pendingCount = groupTimesheets(timesheets.filter(t => t.status === 'submitted')).length;

  if (!canApprove) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
          <h2 className="text-xl font-semibold">Access Denied</h2>
          <p className="text-muted-foreground mt-2">You don't have permission to approve timesheets</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in overflow-x-hidden" data-testid="approvals-page">
      {/* Banner */}
      <div className="relative overflow-hidden rounded-2xl border border-border bg-gradient-to-br from-blue-600/10 via-indigo-500/5 to-violet-400/10 p-6">
        <div className="pointer-events-none absolute -top-10 -right-10 w-48 h-48 rounded-full bg-primary/5 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-8 -left-8 w-36 h-36 rounded-full bg-primary/5 blur-2xl" />
        <div className="relative flex flex-col sm:flex-row sm:items-center sm:justify-between gap-5">
          <div className="flex items-center gap-4">
            <div className="flex-shrink-0 w-14 h-14 rounded-2xl bg-background/70 backdrop-blur-sm border border-border flex items-center justify-center shadow-sm">
              <CheckCircle className="w-7 h-7 text-primary" />
            </div>
            <div>
              <div className="flex items-center gap-2 mb-0.5">
                <span className="inline-block w-2 h-2 rounded-full bg-primary animate-pulse" />
                <span className="text-xs font-medium text-muted-foreground uppercase tracking-widest">Timesheet Reviews</span>
              </div>
              <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">
                <span className="bg-gradient-to-r from-primary to-violet-500 bg-clip-text text-transparent">Approvals</span>
              </h1>
              <p className="text-sm text-muted-foreground mt-0.5 flex items-center gap-1.5">
                <Sparkles className="w-3.5 h-3.5 text-primary/60" />
                Review and approve team timesheets
              </p>
            </div>
          </div>
          <Button variant="outline" size="sm" className="gap-2 bg-background/70 backdrop-blur-sm flex-shrink-0" onClick={fetchData} disabled={loading}>
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-tour="approvals-stats">
        <Card className="card-hover">
          <CardContent className="p-6 flex items-center gap-4">
            <div className="w-12 h-12 bg-orange-500/10 rounded-sm flex items-center justify-center">
              <Clock className="w-6 h-6 text-orange-500" />
            </div>
            <div>
              <div className="text-2xl font-bold">{pendingCount}</div>
              <div className="text-sm text-muted-foreground">Pending Approval</div>
            </div>
          </CardContent>
        </Card>
        <Card className="card-hover">
          <CardContent className="p-6 flex items-center gap-4">
            <div className="w-12 h-12 bg-green-500/10 rounded-sm flex items-center justify-center">
              <CheckCircle className="w-6 h-6 text-green-500" />
            </div>
            <div>
              <div className="text-2xl font-bold">{timesheets.filter(t => t.status === 'approved').length}</div>
              <div className="text-sm text-muted-foreground">Approved</div>
            </div>
          </CardContent>
        </Card>
        <Card className="card-hover">
          <CardContent className="p-6 flex items-center gap-4">
            <div className="w-12 h-12 bg-red-500/10 rounded-sm flex items-center justify-center">
              <XCircle className="w-6 h-6 text-red-500" />
            </div>
            <div>
              <div className="text-2xl font-bold">{timesheets.filter(t => t.status === 'rejected').length}</div>
              <div className="text-sm text-muted-foreground">Rejected</div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* User Filter */}
      {userOptions.length > 1 && (
        <div className="flex items-center gap-3 flex-wrap" data-tour="approvals-member-filter">
          <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
            <Users className="w-4 h-4" />
            <span>Member</span>
          </div>
          <Select value={selectedUserId} onValueChange={setSelectedUserId}>
            <SelectTrigger className="w-[220px] h-9 text-sm">
              <SelectValue placeholder="All members" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All members</SelectItem>
              {userOptions.map(u => (
                <SelectItem key={u.id} value={u.id}>
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 bg-muted rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">
                      {u.first_name?.[0]}{u.last_name?.[0]}
                    </div>
                    {u.first_name} {u.last_name}
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {selectedUserId && selectedUserId !== 'all' && (
            <button
              onClick={() => setSelectedUserId('all')}
              className="flex items-center gap-1 text-xs bg-accent/10 text-accent hover:bg-accent/20 px-2 py-1 rounded-full transition-colors"
            >
              {users[selectedUserId]?.first_name} {users[selectedUserId]?.last_name}
              <X className="w-3 h-3" />
            </button>
          )}
        </div>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList data-tour="approvals-tabs">
          <TabsTrigger value="pending" className="gap-2">
            Pending {pendingCount > 0 && <Badge variant="secondary">{pendingCount}</Badge>}
          </TabsTrigger>
          <TabsTrigger value="approved">Approved</TabsTrigger>
          <TabsTrigger value="rejected">Rejected</TabsTrigger>
        </TabsList>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="w-8 h-8 border-2 border-accent border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          ['pending', 'approved', 'rejected'].map(tab => (
            <TabsContent key={tab} value={tab} className="space-y-4" data-tour="timesheet-list">
              {filterTimesheets(tab).length === 0 ? (
                <Card>
                  <CardContent className="p-8 text-center text-muted-foreground">
                    {tab === 'pending' ? (
                      <><CheckCircle className="w-12 h-12 mx-auto mb-4 text-green-500" /><p>All caught up! No pending timesheets.</p></>
                    ) : tab === 'approved' ? (
                      <><CheckCircle className="w-12 h-12 mx-auto mb-4 text-muted-foreground/30" /><p>No approved timesheets.</p></>
                    ) : (
                      <><XCircle className="w-12 h-12 mx-auto mb-4 text-muted-foreground/30" /><p>No rejected timesheets.</p></>
                    )}
                  </CardContent>
                </Card>
              ) : (
                groupTimesheets(filterTimesheets(tab)).map((group) => (
                  <TimesheetCard
                    key={group.key}
                    timesheet={group}
                    user={users[group.user_id]}
                    users={users}
                    showActions={tab === 'pending' && group.user_id !== user?.id}
                    expanded={expandedId === group.key}
                    detail={detailData[group.key]}
                    detailLoading={detailLoading[group.key]}
                    approving={approvingId === group.key}
                    onToggleExpand={() => toggleExpand(group.key, group.timesheets.map(t => t.id))}
                    onApprove={() => handleApprove(group.key, group.timesheets.map(t => t.id))}
                    onReject={(compiledReason, flaggedEntries) => openRejectDialog(group, compiledReason, flaggedEntries)}
                    onEditEntry={openEditEntry}
                    onDeleteEntry={handleDeleteEntry}
                    onViewWorkflow={() => setWorkflowTimesheet(group.timesheets[0])}
                  />
                ))
              )}
            </TabsContent>
          ))
        )}
      </Tabs>

      {/* Reject Dialog */}
      <Dialog open={showRejectDialog} onOpenChange={setShowRejectDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject Timesheet</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <p className="text-sm text-muted-foreground">
              Please provide a reason for rejecting this timesheet. The employee will be notified.
            </p>
            <Textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Enter rejection reason..."
              rows={4}
              data-testid="reject-reason-input"
            />
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowRejectDialog(false)} disabled={rejectLoading}>Cancel</Button>
              <Button variant="destructive" onClick={handleReject} disabled={rejectLoading} data-testid="confirm-reject-btn">
                {rejectLoading ? <><span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin inline-block mr-2" />Rejecting...</> : 'Reject Timesheet'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Workflow History Dialog */}
      <WorkflowHistoryDialog
        timesheet={workflowTimesheet}
        users={users}
        onClose={() => setWorkflowTimesheet(null)}
      />

      {/* Delete Entry Dialog */}
      <AlertDialog open={!!deleteEntry} onOpenChange={(open) => { if (!open) setDeleteEntry(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Trash2 className="w-4 h-4 text-destructive" /> Delete Time Entry
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-1">
              <span>Are you sure you want to delete this entry? This cannot be undone.</span>
              {deleteEntry && (
                <div className="mt-3 p-3 rounded-md bg-muted text-sm text-foreground space-y-1">
                  <div><span className="text-muted-foreground">Project:</span> <span className="font-medium">{deleteEntry.project_name}</span></div>
                  <div><span className="text-muted-foreground">Date:</span> {deleteEntry.date}</div>
                  <div><span className="text-muted-foreground">Hours:</span> <span className="font-mono">{fmtHHMM(deleteEntry.hours || 0)}h</span></div>
                  {deleteEntry.description && <div><span className="text-muted-foreground">Description:</span> {deleteEntry.description}</div>}
                </div>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteLoading}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDeleteEntry}
              disabled={deleteLoading}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteLoading ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Edit Entry Dialog */}
      <Dialog open={!!editEntry} onOpenChange={(open) => { if (!open) setEditEntry(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Time Entry</DialogTitle>
          </DialogHeader>
          {editEntry && (
            <div className="space-y-4 mt-2">
              <div className="text-sm text-muted-foreground">
                <span className="font-medium text-foreground">{editEntry.project_name}</span>
                {' — '}{editEntry.date}
              </div>
              <div className="space-y-1">
                <Label>Hours</Label>
                <Input
                  type="number"
                  step="0.25"
                  min="0.25"
                  max="24"
                  value={editForm.hours}
                  onChange={e => setEditForm(f => ({ ...f, hours: e.target.value }))}
                />
              </div>
              <div className="space-y-1">
                <Label>Description</Label>
                <Textarea
                  value={editForm.description}
                  onChange={e => setEditForm(f => ({ ...f, description: e.target.value }))}
                  placeholder="What was worked on..."
                  rows={3}
                />
              </div>
              <div className="space-y-1">
                <Label>Blockers or Challenges</Label>
                <Textarea
                  value={editForm.blockers}
                  onChange={e => setEditForm(f => ({ ...f, blockers: e.target.value }))}
                  placeholder="Any blockers or challenges? (optional)"
                  rows={2}
                  className="focus-visible:ring-destructive/50 placeholder:text-muted-foreground/50"
                />
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="is_billable"
                  checked={editForm.is_billable}
                  onChange={e => setEditForm(f => ({ ...f, is_billable: e.target.checked }))}
                  className="w-4 h-4"
                />
                <Label htmlFor="is_billable">Billable</Label>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setEditEntry(null)}>Cancel</Button>
                <Button onClick={saveEditEntry} disabled={editSaving}>
                  {editSaving ? 'Saving...' : 'Save Changes'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

const TimesheetCard = ({
  timesheet, user, showActions, expanded, detail, detailLoading, approving,
  onToggleExpand, onApprove, onReject, onEditEntry, onDeleteEntry, onViewWorkflow,
}) => {
  const { isAdmin, isSuperAdmin } = useAuth();
  const canAdminDelete = isAdmin || isSuperAdmin;
  const [entryFlags, setEntryFlags] = useState({});

  const today = new Date().toISOString().split('T')[0];
  const isCurrentWeek = timesheet.week_start <= today && today <= timesheet.week_end;

  const setEntryFlag = (eid, status) => {
    setEntryFlags(prev => ({
      ...prev,
      [eid]: { status, reason: prev[eid]?.reason || '' },
    }));
  };

  const updateEntryReason = (eid, reason) => {
    setEntryFlags(prev => ({
      ...prev,
      [eid]: { ...prev[eid], reason },
    }));
  };

  const getCompiledReason = () => {
    const flagged = Object.entries(entryFlags).filter(([, v]) => v?.status === 'rejected');
    if (flagged.length === 0) return '';
    const lines = flagged.map(([eid, v]) => {
      const entry = detail?.entries?.find(e => e.id === eid);
      const name = entry ? `${entry.date} – ${entry.project_name}` : eid;
      return v.reason ? `• ${name}: ${v.reason}` : `• ${name}`;
    });
    return `Flagged entries:\n${lines.join('\n')}`;
  };

  const flaggedCount = Object.values(entryFlags).filter(v => v?.status === 'rejected').length;

  const getFlaggedEntries = () =>
    Object.entries(entryFlags)
      .filter(([, v]) => v?.status === 'rejected')
      .map(([eid, v]) => ({ entry_id: eid, reason: v.reason || '' }));

  const getStatusBadge = (status) => {
    const styles = {
      draft: 'status-draft',
      submitted: 'status-submitted',
      approved: 'status-approved',
      rejected: 'status-rejected',
    };
    return <span className={`status-badge ${styles[status]}`}>{status}</span>;
  };

  return (
    <Card className={`card-hover overflow-hidden transition-colors ${isCurrentWeek ? 'border-primary/40 shadow-sm shadow-primary/10' : ''}`} data-testid={`timesheet-card-${timesheet.id}`}>
      <CardContent className="p-4 sm:p-6">

        {/* ── Desktop header (sm+) ── */}
        <div className="hidden sm:flex items-start justify-between">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-muted rounded-sm flex items-center justify-center text-lg font-bold">
              {user?.first_name?.[0]}{user?.last_name?.[0]}
            </div>
            <div>
              <div className="font-medium text-lg">{user?.first_name} {user?.last_name}</div>
              <div className="flex items-center gap-4 mt-1 text-sm text-muted-foreground">
                <span className="flex items-center gap-1"><User className="w-4 h-4" />{user?.department?.replace('_', ' ')}</span>
                <span className="flex items-center gap-1.5">
                    <Calendar className="w-4 h-4" />{timesheet.week_start} — {timesheet.week_end}
                    {isCurrentWeek && (
                      <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-primary/10 text-primary border border-primary/25">
                        <span className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse" />
                        This Week
                      </span>
                    )}
                  </span>
              </div>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <div className="text-right">
              {getStatusBadge(timesheet.status)}
              <div className="mt-2 text-2xl font-bold font-mono">{fmtHHMM(timesheet.total_hours || 0)}h</div>
              <div className="text-sm text-muted-foreground">{fmtHHMM(timesheet.billable_hours || 0)}h billable</div>
            </div>
            <button onClick={onToggleExpand} className="mt-1 p-1 rounded hover:bg-muted transition-colors text-muted-foreground">
              {expanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* ── Mobile header ── */}
        <div className="sm:hidden">
          <div className="flex items-start justify-between gap-2">
            {/* Left: avatar + name + meta */}
            <div className="flex items-start gap-3 min-w-0">
              <div className="w-10 h-10 bg-muted rounded-sm flex items-center justify-center text-base font-bold flex-shrink-0">
                {user?.first_name?.[0]}{user?.last_name?.[0]}
              </div>
              <div className="min-w-0">
                <div className="font-medium text-base truncate">{user?.first_name} {user?.last_name}</div>
                <div className="flex items-center gap-1 mt-0.5 text-xs text-muted-foreground">
                  <User className="w-3 h-3 flex-shrink-0" />{user?.department?.replace('_', ' ')}
                </div>
                <div className="flex items-center gap-1 mt-0.5 text-xs text-muted-foreground flex-wrap">
                  <Calendar className="w-3 h-3 flex-shrink-0" />{timesheet.week_start} — {timesheet.week_end}
                  {isCurrentWeek && (
                    <span className="inline-flex items-center gap-1 text-[9px] font-semibold px-1.5 py-0.5 rounded-full bg-primary/10 text-primary border border-primary/25 flex-shrink-0">
                      <span className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse" />
                      This Week
                    </span>
                  )}
                </div>
              </div>
            </div>
            {/* Right: status + hours + chevron */}
            <div className="flex items-start gap-1.5 flex-shrink-0">
              <div className="text-right">
                {getStatusBadge(timesheet.status)}
                <div className="mt-1 text-xl font-bold font-mono">{fmtHHMM(timesheet.total_hours || 0)}h</div>
                <div className="text-xs text-muted-foreground">{fmtHHMM(timesheet.billable_hours || 0)}h billable</div>
              </div>
              <button onClick={onToggleExpand} className="mt-1 p-1 rounded hover:bg-muted transition-colors text-muted-foreground">
                {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </button>
            </div>
          </div>
        </div>

        {timesheet.recall_reason && (
          <div className="mt-4 p-3 bg-amber-500/10 border border-amber-500/20 rounded-sm flex gap-2">
            <Undo2 className="w-4 h-4 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-amber-600 dark:text-amber-400">Previously Recalled</div>
              <div className="text-sm mt-0.5 text-muted-foreground">{timesheet.recall_reason}</div>
              {detail?.projectBreakdown?.length > 0 && (
                <div className="mt-1.5 flex flex-wrap gap-1">
                  {detail.projectBreakdown.map((p, i) => (
                    <span key={i} className="inline-flex items-center text-xs bg-amber-500/15 text-amber-700 dark:text-amber-300 px-2 py-0.5 rounded-full font-medium">
                      {p.project_name}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {timesheet.rejection_reason && (
          <div className="mt-4 p-3 bg-red-500/10 border border-red-500/20 rounded-sm">
            <div className="text-sm font-medium text-red-600 dark:text-red-400">Rejection Reason:</div>
            <div className="text-sm mt-1">{timesheet.rejection_reason}</div>
          </div>
        )}

        {/* Expanded detail */}
        {expanded && (
          <div className="mt-4 pt-4 border-t border-border space-y-4">
            {detailLoading ? (
              <div className="flex items-center justify-center py-6">
                <div className="w-6 h-6 border-2 border-accent border-t-transparent rounded-full animate-spin" />
              </div>
            ) : detail ? (
              <>
                {/* Project breakdown */}
                <div>
                  <div className="text-sm font-semibold mb-2 flex items-center gap-1">
                    <Briefcase className="w-4 h-4" /> Project Breakdown
                  </div>
                  <div className="rounded-md border border-border overflow-x-auto">
                    <table className="w-full text-sm min-w-[280px]">
                      <thead className="bg-muted/50">
                        <tr>
                          <th className="text-left px-3 py-2 font-medium">Project</th>
                          <th className="text-right px-3 py-2 font-medium">Hours</th>
                          <th className="text-right px-3 py-2 font-medium">Billable</th>
                        </tr>
                      </thead>
                      <tbody>
                        {detail.projectBreakdown.map((p, i) => (
                          <tr key={p.project_id || i} className="border-t border-border">
                            <td className="px-3 py-2">{p.project_name}</td>
                            <td className="px-3 py-2 text-right font-mono">{fmtHHMM(p.total_hours)}h</td>
                            <td className="px-3 py-2 text-right font-mono text-muted-foreground">{fmtHHMM(p.billable_hours)}h</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Individual entries */}
                <div>
                  <div className="text-sm font-semibold mb-2 flex items-center gap-1">
                    <Clock className="w-4 h-4" /> Time Entries
                  </div>

                  {/* Mobile: date-grouped cards */}
                  {detail.entries.length === 0 ? (
                    <p className="text-sm text-muted-foreground px-1">No entries</p>
                  ) : (
                    <>
                      {/* Mobile card view */}
                      <div className="sm:hidden space-y-3">
                        {Object.entries(
                          detail.entries.reduce((acc, e) => {
                            if (!acc[e.date]) acc[e.date] = [];
                            acc[e.date].push(e);
                            return acc;
                          }, {})
                        ).sort(([a], [b]) => a.localeCompare(b)).map(([date, entries]) => (
                          <div key={date}>
                            <div className="flex items-center gap-1.5 px-2 py-1.5 bg-muted/50 rounded-md mb-1.5">
                              <Calendar className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
                              <span className="text-sm font-medium">{date}</span>
                            </div>
                            <div className="space-y-2">
                              {entries.map((entry) => {
                                const flag = entryFlags[entry.id];
                                return (
                                  <div key={entry.id} className={`border rounded-md p-3 transition-colors ${flag?.status === 'rejected' ? 'border-red-300 bg-red-500/5' : flag?.status === 'ok' ? 'border-green-300 bg-green-500/5' : 'border-border'}`}>
                                    <div className="flex items-start justify-between gap-2">
                                      <div className="space-y-1.5 flex-1 min-w-0">
                                        <div className="flex gap-2 text-sm">
                                          <span className="text-muted-foreground w-24 flex-shrink-0">Project</span>
                                          <span className="font-medium break-words min-w-0 flex-1">{entry.project_name}</span>
                                        </div>
                                        <div className="flex gap-2 text-sm">
                                          <span className="text-muted-foreground w-24 flex-shrink-0">Description</span>
                                          <span className="text-muted-foreground italic text-xs break-words min-w-0 flex-1">
                                            {entry.description || 'No note'}
                                          </span>
                                        </div>
                                        {entry.blockers && (
                                          <div className="flex gap-2 text-sm">
                                            <span className="text-muted-foreground w-24 flex-shrink-0">Blockers</span>
                                            <span className="text-destructive text-xs break-words min-w-0 flex-1">⚠ {entry.blockers}</span>
                                          </div>
                                        )}
                                        <div className="flex gap-2 text-sm">
                                          <span className="text-muted-foreground w-24 flex-shrink-0">Hours</span>
                                          <span className="font-mono">{fmtHHMM(entry.hours || 0)}h</span>
                                        </div>
                                        <div className="flex gap-2 text-sm items-center">
                                          <span className="text-muted-foreground w-24 flex-shrink-0">Billable</span>
                                          {entry.is_billable !== false
                                            ? <span className="text-green-600 text-xs font-medium bg-green-500/10 px-2 py-0.5 rounded">Yes</span>
                                            : <span className="text-muted-foreground text-xs">No</span>
                                          }
                                        </div>
                                        {showActions && (
                                          <div className="flex items-center gap-2 pt-1">
                                            <button
                                              onClick={() => setEntryFlag(entry.id, flag?.status === 'ok' ? null : 'ok')}
                                              className={`flex items-center gap-1 px-2 py-1 rounded text-xs transition-colors ${flag?.status === 'ok' ? 'text-green-600 bg-green-500/10' : 'text-muted-foreground hover:text-green-600 hover:bg-green-500/10'}`}
                                            >
                                              <CheckCircle className="w-3.5 h-3.5" /> Accept
                                            </button>
                                            <button
                                              onClick={() => setEntryFlag(entry.id, flag?.status === 'rejected' ? null : 'rejected')}
                                              className={`flex items-center gap-1 px-2 py-1 rounded text-xs transition-colors ${flag?.status === 'rejected' ? 'text-red-600 bg-red-500/10' : 'text-muted-foreground hover:text-red-600 hover:bg-red-500/10'}`}
                                            >
                                              <XCircle className="w-3.5 h-3.5" /> Reject
                                            </button>
                                          </div>
                                        )}
                                        {showActions && flag?.status === 'rejected' && (
                                          <div className="flex items-center gap-2 pt-0.5">
                                            <span className="text-xs text-red-500 whitespace-nowrap">Reason:</span>
                                            <Input
                                              className="h-7 text-xs border-red-200 focus-visible:ring-red-300"
                                              placeholder="Reason (optional)..."
                                              value={flag.reason || ''}
                                              onChange={e => updateEntryReason(entry.id, e.target.value)}
                                            />
                                          </div>
                                        )}
                                      </div>
                                      {(timesheet.status !== 'approved' || canAdminDelete) && (
                                        <div className="flex gap-1 flex-shrink-0">
                                          {timesheet.status !== 'approved' && (
                                            <button
                                              onClick={() => onEditEntry(entry)}
                                              className="p-1 rounded hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
                                              title="Edit entry"
                                            >
                                              <Pencil className="w-3.5 h-3.5" />
                                            </button>
                                          )}
                                          <button
                                            onClick={() => onDeleteEntry(entry)}
                                            className="p-1 rounded hover:bg-muted transition-colors text-muted-foreground hover:text-destructive"
                                            title="Delete entry"
                                          >
                                            <Trash2 className="w-3.5 h-3.5" />
                                          </button>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        ))}
                      </div>

                      {/* Desktop table view */}
                      <div className="hidden sm:block rounded-md border border-border overflow-x-auto max-w-full">
                        <table className="w-full text-sm min-w-[400px]">
                          <thead className="bg-muted/50">
                            <tr>
                              <th className="text-left px-3 py-2 font-medium">Date</th>
                              <th className="text-left px-3 py-2 font-medium">Project</th>
                              <th className="text-left px-3 py-2 font-medium">Description</th>
                              <th className="text-left px-3 py-2 font-medium">Blockers</th>
                              <th className="text-right px-3 py-2 font-medium">Hours</th>
                              <th className="text-center px-3 py-2 font-medium">Billable</th>
                              {showActions && <th className="text-center px-3 py-2 font-medium w-20" data-tour="entry-flags">Review</th>}
                              <th className="px-3 py-2"></th>
                            </tr>
                          </thead>
                          <tbody>
                            {detail.entries.map((entry) => {
                              const flag = entryFlags[entry.id];
                              return (
                                <React.Fragment key={entry.id}>
                                  <tr className={`border-t border-border transition-colors ${flag?.status === 'rejected' ? 'bg-red-500/5' : flag?.status === 'ok' ? 'bg-green-500/5' : 'hover:bg-muted/30'}`}>
                                    <td className="px-3 py-2 whitespace-nowrap">{entry.date}</td>
                                    <td className="px-3 py-2">{entry.project_name}</td>
                                    <td className="px-3 py-2">
                                      {entry.description ? (
                                        <TooltipProvider>
                                          <Tooltip>
                                            <TooltipTrigger asChild>
                                              <div className="flex items-start gap-1.5 cursor-default max-w-[300px]">
                                                <MessageSquare className="w-3.5 h-3.5 text-accent flex-shrink-0 mt-0.5" />
                                                <span className="text-sm text-foreground break-words whitespace-normal">{entry.description}</span>
                                              </div>
                                            </TooltipTrigger>
                                            <TooltipContent side="top" className="max-w-xs whitespace-pre-wrap">
                                              {entry.description}
                                            </TooltipContent>
                                          </Tooltip>
                                        </TooltipProvider>
                                      ) : (
                                        <span className="text-muted-foreground/40 text-xs italic">No note</span>
                                      )}
                                    </td>
                                    <td className="px-3 py-2">
                                      {entry.blockers ? (
                                        <div className="flex items-start gap-1.5 max-w-[220px]">
                                          <span className="text-destructive mt-0.5 flex-shrink-0">⚠</span>
                                          <span className="text-sm text-destructive break-words">{entry.blockers}</span>
                                        </div>
                                      ) : (
                                        <span className="text-muted-foreground/40 text-xs italic">—</span>
                                      )}
                                    </td>
                                    <td className="px-3 py-2 text-right font-mono">{fmtHHMM(entry.hours || 0)}h</td>
                                    <td className="px-3 py-2 text-center">
                                      {entry.is_billable !== false
                                        ? <span className="text-green-600 text-xs font-medium">Yes</span>
                                        : <span className="text-muted-foreground text-xs">No</span>
                                      }
                                    </td>
                                    {showActions && (
                                      <td className="px-3 py-2">
                                        <div className="flex gap-1 justify-center">
                                          <button
                                            onClick={() => setEntryFlag(entry.id, flag?.status === 'ok' ? null : 'ok')}
                                            className={`p-1 rounded transition-colors ${flag?.status === 'ok' ? 'text-green-600 bg-green-500/10' : 'text-muted-foreground hover:text-green-600'}`}
                                            title="Accept this entry"
                                          >
                                            <CheckCircle className="w-4 h-4" />
                                          </button>
                                          <button
                                            onClick={() => setEntryFlag(entry.id, flag?.status === 'rejected' ? null : 'rejected')}
                                            className={`p-1 rounded transition-colors ${flag?.status === 'rejected' ? 'text-red-600 bg-red-500/10' : 'text-muted-foreground hover:text-red-600'}`}
                                            title="Flag this entry for rejection"
                                          >
                                            <XCircle className="w-4 h-4" />
                                          </button>
                                        </div>
                                      </td>
                                    )}
                                    <td className="px-3 py-2 text-right">
                                      <div className="flex gap-1 justify-end">
                                        {timesheet.status !== 'approved' && (
                                          <button
                                            onClick={() => onEditEntry(entry)}
                                            className="p-1 rounded hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
                                            title="Edit entry"
                                          >
                                            <Pencil className="w-3.5 h-3.5" />
                                          </button>
                                        )}
                                        {(timesheet.status !== 'approved' || canAdminDelete) && (
                                          <button
                                            onClick={() => onDeleteEntry(entry)}
                                            className="p-1 rounded hover:bg-muted transition-colors text-muted-foreground hover:text-destructive"
                                            title="Delete entry"
                                          >
                                            <Trash2 className="w-3.5 h-3.5" />
                                          </button>
                                        )}
                                      </div>
                                    </td>
                                  </tr>
                                  {showActions && flag?.status === 'rejected' && (
                                    <tr className="bg-red-500/5 border-t border-red-500/10">
                                      <td colSpan={8} className="px-3 pb-2.5 pt-1">
                                        <div className="flex items-center gap-2">
                                          <span className="text-xs text-red-500 whitespace-nowrap">Rejection reason:</span>
                                          <Input
                                            className="h-7 text-xs border-red-200 focus-visible:ring-red-300"
                                            placeholder="Reason for flagging this entry (optional)..."
                                            value={flag.reason || ''}
                                            onChange={e => updateEntryReason(entry.id, e.target.value)}
                                          />
                                        </div>
                                      </td>
                                    </tr>
                                  )}
                                </React.Fragment>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </>
                  )}
                </div>
              </>
            ) : null}
          </div>
        )}

        {/* Footer */}
        <div className="mt-4 pt-4 border-t border-border flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <button
            onClick={onViewWorkflow}
            className="w-full sm:w-auto flex items-center justify-between sm:justify-start sm:gap-1.5 text-sm text-primary hover:underline"
          >
            <span className="flex items-center gap-1.5">
              <History className="w-4 h-4" /> View Workflow History
            </span>
            <ChevronDown className="w-4 h-4 sm:hidden -rotate-90" />
          </button>
          {showActions && (
            <div className="flex flex-wrap items-center gap-2 sm:ml-auto" data-tour="approve-reject-actions">
              {flaggedCount > 0 && (
                <span className="text-xs text-red-500 flex items-center gap-1 flex-shrink-0">
                  <XCircle className="w-3 h-3" />
                  {flaggedCount} entr{flaggedCount > 1 ? 'ies' : 'y'} flagged — resolve before approving
                </span>
              )}
              <div className="flex gap-2 flex-1 sm:flex-none">
                <Button variant="outline" onClick={() => onReject(getCompiledReason(), getFlaggedEntries())} disabled={approving} className="gap-2 flex-1 sm:flex-none" data-testid="reject-btn">
                  <XCircle className="w-4 h-4" /> Reject
                </Button>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="flex-1 sm:flex-none">
                        <Button
                          onClick={onApprove}
                          disabled={approving || flaggedCount > 0}
                          className="gap-2 w-full bg-green-600 hover:bg-green-700 text-white disabled:opacity-50 disabled:cursor-not-allowed"
                          data-testid="approve-btn"
                        >
                          {approving
                            ? <><span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Approving...</>
                            : <><CheckCircle className="w-4 h-4" /> Approve</>
                          }
                        </Button>
                      </span>
                    </TooltipTrigger>
                    {flaggedCount > 0 && (
                      <TooltipContent side="top">
                        Clear all flagged entries (✗) before approving
                      </TooltipContent>
                    )}
                  </Tooltip>
                </TooltipProvider>
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

const WorkflowHistoryDialog = ({ timesheet, users, onClose }) => {
  if (!timesheet) return null;

  const employee = users[timesheet.user_id];
  const employeeName = employee ? `${employee.first_name} ${employee.last_name}` : 'Unknown';

  // Find who it's pending with: admins/super_admins in same department (or manager)
  const pendingWith = Object.values(users).filter(u => {
    if (u.id === timesheet.user_id) return false;
    if (u.role === 'super_admin') return true;
    if (u.role === 'admin' && u.department === employee?.department) return true;
    if (employee?.manager_id && u.id === employee.manager_id) return true;
    return false;
  });

  const approver = timesheet.approved_by ? users[timesheet.approved_by] : null;
  const approverName = approver ? `${approver.first_name} ${approver.last_name}` : 'Unknown';

  const formatDate = (iso) => {
    if (!iso) return '—';
    return new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
  };

  const steps = [
    {
      icon: <User className="w-4 h-4" />,
      color: 'bg-blue-500',
      label: 'Submitted',
      by: employeeName,
      at: formatDate(timesheet.submitted_at),
      done: true,
    },
    ...(timesheet.recall_reason ? [{
      icon: <Undo2 className="w-4 h-4" />,
      color: 'bg-amber-500',
      label: 'Recalled',
      by: employeeName,
      at: formatDate(timesheet.recalled_at),
      done: true,
      note: `Reason: ${timesheet.recall_reason}`,
    }] : []),
    {
      icon: timesheet.status === 'approved'
        ? <CheckCircle className="w-4 h-4" />
        : timesheet.status === 'rejected'
          ? <XCircle className="w-4 h-4" />
          : <Clock className="w-4 h-4" />,
      color: timesheet.status === 'approved'
        ? 'bg-green-500'
        : timesheet.status === 'rejected'
          ? 'bg-red-500'
          : 'bg-orange-400',
      label: timesheet.status === 'approved'
        ? 'Approved'
        : timesheet.status === 'rejected'
          ? 'Rejected'
          : 'Pending Approval',
      by: timesheet.status === 'submitted'
        ? pendingWith.length > 0
          ? pendingWith.map(u => `${u.first_name} ${u.last_name}`).join(', ')
          : 'Assigned approver'
        : approverName,
      at: timesheet.status === 'submitted' ? 'Awaiting action' : formatDate(timesheet.approved_at),
      done: timesheet.status !== 'submitted',
      note: timesheet.status === 'rejected' && timesheet.rejection_reason
        ? `Reason: ${timesheet.rejection_reason}`
        : null,
    },
  ];

  return (
    <Dialog open={!!timesheet} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <History className="w-5 h-5" /> Workflow History
          </DialogTitle>
        </DialogHeader>
        <div className="mt-2 text-sm text-muted-foreground mb-4">
          {employeeName} — {timesheet.week_start} to {timesheet.week_end}
        </div>
        <div className="space-y-0">
          {steps.map((step, i) => (
            <div key={i} className="flex gap-3">
              {/* Timeline line */}
              <div className="flex flex-col items-center">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white flex-shrink-0 ${step.color}`}>
                  {step.icon}
                </div>
                {i < steps.length - 1 && (
                  <div className="w-px flex-1 bg-border my-1" style={{ minHeight: '24px' }} />
                )}
              </div>
              {/* Content */}
              <div className="pb-5 flex-1 min-w-0">
                <div className="font-medium text-sm">{step.label}</div>
                <div className="text-sm text-muted-foreground mt-0.5">
                  {timesheet.status === 'submitted' && i === 1 ? (
                    <span className="flex items-center gap-1">
                      <Clock className="w-3 h-3 text-orange-400" />
                      Pending with: <span className="font-medium text-foreground ml-1">{step.by}</span>
                    </span>
                  ) : (
                    <span>By: <span className="font-medium text-foreground">{step.by}</span></span>
                  )}
                </div>
                <div className="text-xs text-muted-foreground mt-0.5">{step.at}</div>
                {step.note && (
                  <div className="mt-1 text-xs text-red-500 bg-red-500/10 px-2 py-1 rounded">{step.note}</div>
                )}
              </div>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ApprovalsPage;
